import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { updateApplicationStatus, APPLICATION_STATUS } from '../../services/applicationService';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as XLSX from 'xlsx';

const FranchiseApplications = ({ user, userData }) => {
    const navigate = useNavigate();
    const [applications, setApplications] = useState([]);
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [franchiseId, setFranchiseId] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [jobFilter, setJobFilter] = useState('all');
    const [companyFilter, setCompanyFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);

    useEffect(() => {
        const fetchFranchiseId = async () => {
            if (userData?.franchiseId) {
                setFranchiseId(userData.franchiseId);
            } else if (user?.uid) {
                try {
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    if (userDoc.exists()) {
                        const uData = userDoc.data();
                        setFranchiseId(uData.franchiseId || user.uid);
                    } else {
                        setFranchiseId(user.uid);
                    }
                } catch (error) {
                    console.error("Error getting user franchiseId:", error);
                    setFranchiseId(user.uid);
                }
            }
        };
        fetchFranchiseId();
    }, [user, userData]);

    useEffect(() => {
        if (franchiseId) {
            fetchData();
        }
    }, [franchiseId]);

    useEffect(() => {
        filterApplications();
    }, [applications, statusFilter, jobFilter, companyFilter, searchTerm]);

    const fetchData = async () => {
        setLoading(true);
        try {
            console.log('Fetching applications data for franchise:', franchiseId);
            // 1. Fetch companies for this franchise
            const companiesQuery = query(
                collection(db, 'companies'),
                where('franchiseId', '==', franchiseId)
            );
            const companiesSnapshot = await getDocs(companiesQuery);
            const companiesList = companiesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setCompanies(companiesList);
            const companyIds = companiesList.map(c => c.id);
            console.log('Franchise companies:', companyIds);

            // 2. Fetch jobs from these companies
            const jobsSnapshot = await getDocs(collection(db, 'jobs'));
            const jobsList = jobsSnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(job => companyIds.includes(job.companyId) || companyIds.includes(job.userId));
            setJobs(jobsList);

            // 3. Fetch applications for these companies
            const applicationsSnapshot = await getDocs(collection(db, 'applications'));
            const appsList = applicationsSnapshot.docs
                .map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        appliedAt: data.appliedAt ? (
                            typeof data.appliedAt.toDate === 'function' ? data.appliedAt.toDate() : new Date(data.appliedAt)
                        ) : null,
                        updatedAt: data.updatedAt ? (
                            typeof data.updatedAt.toDate === 'function' ? data.updatedAt.toDate() : new Date(data.updatedAt)
                        ) : null
                    };
                })
                .filter(app => companyIds.includes(app.companyId) || companyIds.includes(app.employerId));

            // Sort by applied date (newest first)
            appsList.sort((a, b) => (b.appliedAt || 0) - (a.appliedAt || 0));
            setApplications(appsList);
            console.log('Found franchise applications:', appsList.length);
        } catch (error) {
            console.error("Error loading applications:", error);
            alert("Failed to load applications. Check console for details.");
        } finally {
            setLoading(false);
        }
    };

    const filterApplications = () => {
        let filtered = [...applications];

        if (statusFilter !== 'all') {
            filtered = filtered.filter(app => app.status === statusFilter);
        }

        if (jobFilter !== 'all') {
            filtered = filtered.filter(app => app.jobId === jobFilter);
        }

        if (companyFilter !== 'all') {
            filtered = filtered.filter(app => app.companyId === companyFilter || app.employerId === companyFilter);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(app =>
                (app.applicantName || '').toLowerCase().includes(term) ||
                (app.applicantEmail || '').toLowerCase().includes(term) ||
                (app.applicantPhone || '').toLowerCase().includes(term) ||
                (app.jobTitle || '').toLowerCase().includes(term) ||
                (app.companyName || '').toLowerCase().includes(term)
            );
        }

        setFilteredApplications(filtered);
    };

    const handleStatusUpdate = async (applicationId, newStatus) => {
        setUpdatingStatus(true);
        try {
            const result = await updateApplicationStatus(applicationId, newStatus, {
                updatedBy: user.uid,
                note: `Status updated to ${newStatus} by Franchise Admin`
            });

            if (result.success) {
                setApplications(prev =>
                    prev.map(app =>
                        app.id === applicationId
                            ? { ...app, status: newStatus, updatedAt: new Date() }
                            : app
                    )
                );
                if (selectedApplication?.id === applicationId) {
                    setSelectedApplication(prev => ({
                        ...prev,
                        status: newStatus,
                        updatedAt: new Date()
                    }));
                }
                setShowStatusModal(false);
                alert('Student status updated successfully!');
            } else {
                alert('Failed to update status: ' + result.error);
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status: ' + error.message);
        } finally {
            setUpdatingStatus(false);
        }
    };

    const getStatusCounts = () => {
        const counts = {
            all: applications.length,
            pending: applications.filter(app => app.status === APPLICATION_STATUS.PENDING).length,
            reviewing: applications.filter(app => app.status === APPLICATION_STATUS.REVIEWING).length,
            shortlisted: applications.filter(app => app.status === APPLICATION_STATUS.SHORTLISTED).length,
            selected: applications.filter(app => app.status === 'selected' || app.status === 'accepted').length,
            rejected: applications.filter(app => app.status === APPLICATION_STATUS.REJECTED).length
        };
        return counts;
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            [APPLICATION_STATUS.PENDING]: {
                bg: 'bg-yellow-100 text-yellow-800 border-yellow-200',
                label: 'Pending Review'
            },
            [APPLICATION_STATUS.REVIEWING]: {
                bg: 'bg-blue-100 text-blue-800 border-blue-200',
                label: 'Under Review'
            },
            [APPLICATION_STATUS.SHORTLISTED]: {
                bg: 'bg-purple-100 text-purple-800 border-purple-200',
                label: 'Shortlisted'
            },
            ['selected']: {
                bg: 'bg-green-100 text-green-800 border-green-200',
                label: 'Selected'
            },
            ['accepted']: {
                bg: 'bg-green-100 text-green-800 border-green-200',
                label: 'Accepted'
            },
            [APPLICATION_STATUS.REJECTED]: {
                bg: 'bg-red-100 text-red-800 border-red-200',
                label: 'Rejected'
            }
        };

        const config = statusConfig[status] || { bg: 'bg-gray-100 text-gray-800 border-gray-200', label: status };
        return (
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${config.bg}`}>
                {config.label}
            </span>
        );
    };

    const exportToExcel = () => {
        if (filteredApplications.length === 0) {
            alert('No applications to export.');
            return;
        }

        try {
            const exportData = filteredApplications.map((app, index) => {
                const skills = app.applicantProfile?.skills ? app.applicantProfile.skills.join(', ') : 'N/A';
                return {
                    'S.No': index + 1,
                    'Candidate Name': app.applicantName || 'N/A',
                    'Email': app.applicantEmail || 'N/A',
                    'Phone': app.applicantPhone || 'N/A',
                    'Location': app.applicantLocation || 'N/A',
                    'Job Title': app.jobTitle || 'N/A',
                    'Company Name': app.companyName || 'N/A',
                    'Status': app.status || 'N/A',
                    'Applied Date': app.appliedAt ? app.appliedAt.toLocaleDateString() : 'N/A',
                    'Skills': skills,
                    'Cover Letter': app.coverLetter || 'N/A',
                    'Resume Link': app.resumeURL || 'N/A'
                };
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(exportData);

            // Set column widths
            const colWidths = [
                { wch: 8 },   // S.No
                { wch: 20 },  // Candidate Name
                { wch: 25 },  // Email
                { wch: 15 },  // Phone
                { wch: 15 },  // Location
                { wch: 20 },  // Job Title
                { wch: 20 },  // Company Name
                { wch: 15 },  // Status
                { wch: 12 },  // Applied Date
                { wch: 30 },  // Skills
                { wch: 40 },  // Cover Letter
                { wch: 40 }   // Resume Link
            ];
            ws['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(wb, ws, 'Franchise_Applications');
            const dateStr = new Date().toISOString().split('T')[0];
            XLSX.writeFile(wb, `Franchise_Applications_${dateStr}.xlsx`);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            alert('Failed to export to Excel.');
        }
    };

    if (loading) return <LoadingSpinner />;

    const statusCounts = getStatusCounts();

    return (
        <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-24 lg:pb-12">
            {/* Header */}
            <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Franchise Job Applications</h1>
                    <p className="text-gray-600 text-sm sm:text-base">
                        Track, manage and update statuses of students applying to jobs from your franchise employers
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={exportToExcel}
                        className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 shadow-md"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export Excel
                    </button>
                    <button
                        onClick={() => navigate('/franchise/dashboard')}
                        className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
                {[
                    { label: 'All Candidates', count: statusCounts.all, filter: 'all', color: 'border-slate-500 text-slate-800 bg-slate-50' },
                    { label: 'Pending', count: statusCounts.pending, filter: APPLICATION_STATUS.PENDING, color: 'border-yellow-500 text-yellow-800 bg-yellow-50' },
                    { label: 'Under Review', count: statusCounts.reviewing, filter: APPLICATION_STATUS.REVIEWING, color: 'border-blue-500 text-blue-800 bg-blue-50' },
                    { label: 'Shortlisted', count: statusCounts.shortlisted, filter: APPLICATION_STATUS.SHORTLISTED, color: 'border-purple-500 text-purple-800 bg-purple-50' },
                    { label: 'Selected', count: statusCounts.selected, filter: 'selected', color: 'border-green-500 text-green-800 bg-green-50' },
                    { label: 'Rejected', count: statusCounts.rejected, filter: APPLICATION_STATUS.REJECTED, color: 'border-red-500 text-red-800 bg-red-50' }
                ].map((item) => (
                    <button
                        key={item.label}
                        onClick={() => setStatusFilter(item.filter)}
                        className={`p-4 rounded-xl border-t-4 shadow-sm text-left transition-all hover:shadow-md ${item.color} ${
                            statusFilter === item.filter ? 'ring-2 ring-indigo-500 shadow-md scale-105' : ''
                        }`}
                    >
                        <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">{item.label}</p>
                        <p className="text-xl sm:text-3xl font-extrabold">{item.count}</p>
                    </button>
                ))}
            </div>

            {/* Filters Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search Candidates</label>
                        <input
                            type="text"
                            placeholder="Name, email, phone, job, company..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Company</label>
                        <select
                            value={companyFilter}
                            onChange={(e) => setCompanyFilter(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                        >
                            <option value="all">All Companies</option>
                            {companies.map(c => (
                                <option key={c.id} value={c.id}>{c.companyName || c.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Job</label>
                        <select
                            value={jobFilter}
                            onChange={(e) => setJobFilter(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                        >
                            <option value="all">All Jobs</option>
                            {jobs.map(j => (
                                <option key={j.id} value={j.id}>{j.title}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setStatusFilter('all');
                                setJobFilter('all');
                                setCompanyFilter('all');
                            }}
                            className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                        >
                            Clear All Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Applications List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                    <h2 className="text-lg font-bold text-gray-900">
                        Applications List ({filteredApplications.length})
                    </h2>
                    <span className="text-xs text-gray-500">Showing filtered results</span>
                </div>

                {filteredApplications.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-100 text-gray-600 text-xs font-semibold uppercase border-b border-gray-200">
                                    <th className="px-6 py-4">Candidate</th>
                                    <th className="px-6 py-4">Applied Job</th>
                                    <th className="px-6 py-4">Company</th>
                                    <th className="px-6 py-4">Date Applied</th>
                                    <th className="px-6 py-4 text-center">Status</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredApplications.map((app) => (
                                    <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-semibold text-gray-900 text-sm">{app.applicantName}</div>
                                            <div className="text-xs text-gray-500">{app.applicantEmail}</div>
                                            <div className="text-xs text-gray-500">{app.applicantPhone}</div>
                                            <div className="text-xs text-gray-400 mt-0.5">📍 {app.applicantLocation || 'N/A'}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-indigo-600">{app.jobTitle}</div>
                                            <div className="text-xs text-gray-400">ID: {app.jobId?.substring(0, 8)}...</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-800">
                                            {app.companyName || 'Unknown Employer'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {app.appliedAt ? app.appliedAt.toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {getStatusBadge(app.status)}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setSelectedApplication(app);
                                                        setShowDetailsModal(true);
                                                    }}
                                                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-md text-xs font-semibold transition-colors"
                                                >
                                                    View Details
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedApplication(app);
                                                        setShowStatusModal(true);
                                                    }}
                                                    className="px-3 py-1.5 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 rounded-md text-xs font-semibold transition-colors border border-yellow-200"
                                                >
                                                    Edit Status
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <div className="text-gray-400 text-5xl mb-3">📭</div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">No applications found</h3>
                        <p className="text-gray-500 text-sm">No job seeker applications match your search criteria.</p>
                    </div>
                )}
            </div>

            {/* Details Modal */}
            {showDetailsModal && selectedApplication && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-100 flex flex-col">
                        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-6 text-white flex justify-between items-start">
                            <div>
                                <h2 className="text-2xl font-bold">{selectedApplication.applicantName}</h2>
                                <p className="text-indigo-100 text-sm">Applied for: <span className="font-semibold">{selectedApplication.jobTitle}</span></p>
                                <p className="text-indigo-200 text-xs mt-0.5">Company: {selectedApplication.companyName}</p>
                            </div>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-white/80 hover:text-white"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1 space-y-6">
                            {/* Status and Action banner */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Current Status</span>
                                    {getStatusBadge(selectedApplication.status)}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setShowStatusModal(true)}
                                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Update Application Status
                                    </button>
                                    {selectedApplication.resumeURL && (
                                        <a
                                            href={selectedApplication.resumeURL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            View Resume
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Candidate info grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Contact Information</h3>
                                    <div className="space-y-2 text-sm text-gray-700">
                                        <p><span className="font-semibold">Email:</span> {selectedApplication.applicantEmail || 'N/A'}</p>
                                        <p><span className="font-semibold">Phone:</span> {selectedApplication.applicantPhone || 'N/A'}</p>
                                        <p><span className="font-semibold">Location:</span> {selectedApplication.applicantLocation || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Job Details</h3>
                                    <div className="space-y-2 text-sm text-gray-700">
                                        <p><span className="font-semibold">Applied Position:</span> {selectedApplication.jobTitle || 'N/A'}</p>
                                        <p><span className="font-semibold">Employer Name:</span> {selectedApplication.companyName || 'N/A'}</p>
                                        <p><span className="font-semibold">Date Applied:</span> {selectedApplication.appliedAt ? selectedApplication.appliedAt.toLocaleString() : 'N/A'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Cover Letter */}
                            {selectedApplication.coverLetter && (
                                <div>
                                    <h3 className="text-sm font-bold text-gray-900 border-b pb-2 mb-3">Cover Letter</h3>
                                    <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-xl whitespace-pre-wrap border border-gray-100">
                                        {selectedApplication.coverLetter}
                                    </p>
                                </div>
                            )}

                            {/* Profile details from applicantProfile */}
                            {selectedApplication.applicantProfile && (
                                <div className="space-y-6">
                                    {/* Skills */}
                                    {selectedApplication.applicantProfile.skills && selectedApplication.applicantProfile.skills.length > 0 && (
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900 border-b pb-2 mb-3">Key Skills</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedApplication.applicantProfile.skills.map(skill => (
                                                    <span key={skill} className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-md border border-indigo-100">
                                                        {skill}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Professional Summary */}
                                    {selectedApplication.applicantProfile.professionalSummary && (
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900 border-b pb-2 mb-3">Professional Summary</h3>
                                            <p className="text-sm text-gray-600 leading-relaxed">
                                                {selectedApplication.applicantProfile.professionalSummary}
                                            </p>
                                        </div>
                                    )}

                                    {/* Experience */}
                                    {selectedApplication.applicantProfile.experience && selectedApplication.applicantProfile.experience.length > 0 && (
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900 border-b pb-2 mb-3">Work Experience</h3>
                                            <div className="space-y-4">
                                                {selectedApplication.applicantProfile.experience.map((exp, idx) => (
                                                    <div key={idx} className="border-l-2 border-indigo-500 pl-4 py-1">
                                                        <h4 className="font-bold text-gray-800 text-sm">{exp.jobTitle}</h4>
                                                        <p className="text-xs font-semibold text-gray-600">{exp.company} • {exp.location}</p>
                                                        <p className="text-xs text-gray-500 mt-1">{exp.startDate} to {exp.isCurrentJob ? 'Present' : exp.endDate}</p>
                                                        {exp.description && <p className="text-xs text-gray-600 mt-1.5">{exp.description}</p>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Education */}
                                    {selectedApplication.applicantProfile.education && selectedApplication.applicantProfile.education.length > 0 && (
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900 border-b pb-2 mb-3">Education</h3>
                                            <div className="space-y-4">
                                                {selectedApplication.applicantProfile.education.map((edu, idx) => (
                                                    <div key={idx} className="border-l-2 border-emerald-500 pl-4 py-1">
                                                        <h4 className="font-bold text-gray-800 text-sm">{edu.degree} in {edu.fieldOfStudy}</h4>
                                                        <p className="text-xs font-semibold text-gray-600">{edu.institution}</p>
                                                        <p className="text-xs text-gray-500 mt-1">Graduation Year: {edu.graduationYear}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-150 flex justify-end">
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg text-sm transition-colors"
                            >
                                Close View
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Status Edit Modal */}
            {showStatusModal && selectedApplication && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Update Candidate Status</h3>
                                <p className="text-xs text-gray-500 mt-0.5">Candidate: {selectedApplication.applicantName}</p>
                            </div>
                            <button
                                onClick={() => setShowStatusModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-gray-600">
                                Select a new application status for <span className="font-semibold">{selectedApplication.applicantName}</span> who applied for <span className="font-semibold">{selectedApplication.jobTitle}</span>.
                            </p>

                            <div className="space-y-2">
                                {[
                                    { status: APPLICATION_STATUS.PENDING, label: 'Pending Review', desc: 'Application received and waiting for screening' },
                                    { status: APPLICATION_STATUS.REVIEWING, label: 'Under Review / Viewed', desc: 'Resume viewed or screening initiated' },
                                    { status: APPLICATION_STATUS.SHORTLISTED, label: 'Shortlisted', desc: 'Shortlist the candidate for interview rounds' },
                                    { status: 'selected', label: 'Selected', desc: 'Select candidate for hiring offer' },
                                    { status: APPLICATION_STATUS.REJECTED, label: 'Rejected', desc: 'Decline candidate application' }
                                ].map((item) => (
                                    <button
                                        key={item.status}
                                        onClick={() => handleStatusUpdate(selectedApplication.id, item.status)}
                                        disabled={updatingStatus}
                                        className={`w-full p-3.5 text-left rounded-xl border transition-all flex items-start gap-3 hover:bg-indigo-50/30 ${
                                            selectedApplication.status === item.status
                                                ? 'bg-indigo-50 border-indigo-400 ring-2 ring-indigo-500/20'
                                                : 'border-gray-200'
                                        }`}
                                    >
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                            selectedApplication.status === item.status ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'
                                        }`}>
                                            {selectedApplication.status === item.status && (
                                                <div className="w-2 h-2 rounded-full bg-white"></div>
                                            )}
                                        </div>
                                        <div>
                                            <span className="font-semibold text-sm text-gray-900 block">{item.label}</span>
                                            <span className="text-xs text-gray-500 block mt-0.5">{item.desc}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-end">
                            <button
                                onClick={() => setShowStatusModal(false)}
                                disabled={updatingStatus}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-gray-700 font-semibold rounded-lg text-sm transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FranchiseApplications;
